import React, { useEffect } from 'react';
import styles from './JuryProjectDetails.module.scss';
import useJuryStore from '../../../../store/juryStore';

/**
 * JuryProjectDetails - read-only details panel for the currently
 * selected project in the Jury Voting flow.
 *
 * Renders the contestant-supplied long-form answers and the project
 * gallery. Used inline below a card on mobile and in the right pane
 * of the split layout on desktop.
 *
 * The `/jury/projects` list endpoint returns a lean payload (no answer
 * fields). We lazily fetch the full project record from the public
 * `/projects/:id` route the first time the user opens its details, and
 * cache it in the jury store so subsequent selections are instant.
 */
function JuryProjectDetails({ project }) {
    const projectId = project?._id;
    const fetchProjectDetails = useJuryStore((s) => s.fetchProjectDetails);
    const cached = useJuryStore((s) => (projectId ? s.projectDetails[projectId] : undefined));

    useEffect(() => {
        if (projectId) fetchProjectDetails(projectId);
    }, [projectId, fetchProjectDetails]);

    if (!project) return null;

    // Merge: list summary fields + lazy-loaded long-form fields.
    // `cached === null` means the request is in flight.
    const isLoading = cached === null;
    const merged = { ...project, ...(cached && typeof cached === 'object' ? cached : {}) };

    const sections = [
        { heading: 'Unser Projekt', body: merged.contestantAnswer1 },
        { heading: 'So würden wir die Fördergelder verwenden', body: merged.contestantAnswer2 },
        { heading: 'Unsere Einrichtung', body: merged.contestantAnswer3 },
    ].filter((s) => s.body && s.body.trim().length > 0);

    const gallery = merged.projectGallery ?? [];

    if (isLoading && sections.length === 0) {
        return (
            <div className={styles.Details}>
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Lädt…</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.Details}>
            {!isLoading && sections.length === 0 && (
                <p className={styles.Empty}>Für diesen Teilnehmer wurden noch keine Projektdetails hinterlegt.</p>
            )}

            {sections.map(({ heading, body }) => (
                <section key={heading} className={styles.Section}>
                    <h3 className={styles.SectionHeading}>{heading}</h3>
                    <p className={styles.SectionBody}>{body}</p>
                </section>
            ))}

            {gallery.length > 0 && (
                <div className={styles.Gallery}>
                    {gallery.map((img, i) => (
                        <div key={`${img.url}-${i}`} className={styles.GalleryItem}>
                            <img
                                src={img.url}
                                alt={img.alt || merged.contestantName}
                                className={styles.GalleryImage}
                                loading="lazy"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default JuryProjectDetails;
